// Admin API client. All routes are gated by requireAdmin on the
// backend, which re-reads aj-users.isAdmin on every call — so a
// demoted admin loses access without waiting for their token to
// expire. The Bearer token is the same one issued by /auth/login.

import { getToken } from './auth';

const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');

async function adminFetch(path, opts = {}) {
  const token = getToken();
  if (!token) {
    const err = new Error('Sign in required.');
    err.status = 401;
    err.code = 'UNAUTHORIZED';
    throw err;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
  let data = null;
  try { data = await res.json(); } catch {}
  if (!res.ok) {
    const err = new Error(data?.error?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = data?.error?.code;
    throw err;
  }
  return data;
}

export async function listPending() {
  const data = await adminFetch('/api/admin/companions/pending');
  return data?.companions || [];
}

export async function getCompanionForReview(ownerEmail, companionId) {
  const data = await adminFetch(
    `/api/admin/companions/${encodeURIComponent(ownerEmail)}/${encodeURIComponent(companionId)}`,
  );
  return data?.companion || null;
}

export async function approveCompanion(ownerEmail, companionId) {
  const data = await adminFetch(
    `/api/admin/companions/${encodeURIComponent(ownerEmail)}/${encodeURIComponent(companionId)}/approve`,
    { method: 'POST' },
  );
  return data?.companion || null;
}

export async function rejectCompanion(ownerEmail, companionId, reason) {
  const data = await adminFetch(
    `/api/admin/companions/${encodeURIComponent(ownerEmail)}/${encodeURIComponent(companionId)}/reject`,
    { method: 'POST', body: JSON.stringify({ reason: reason || '' }) },
  );
  return data?.companion || null;
}

// ── Manage-all (admin's full character list) ────────────────────────

export async function listAllForAdmin() {
  const data = await adminFetch('/api/admin/companions');
  return data?.companions || [];
}

// Tag a character into a public-grid category (or pass null to clear).
// Cheap PATCH — only writes the one field.
export async function adminSetCategory(ownerEmail, companionId, category) {
  const data = await adminFetch(
    `/api/admin/companions/${encodeURIComponent(ownerEmail)}/${encodeURIComponent(companionId)}/category`,
    { method: 'PATCH', body: JSON.stringify({ category: category || null }) },
  );
  return data?.companion || null;
}

// Full edit — admin can patch any field on any character regardless
// of ownership / visibility. Mirrors the creator-facing PATCH shape.
export async function adminUpdateCompanion(ownerEmail, companionId, fields = {}) {
  const { name, description, traits, appearance, avatarUrl, category, regeneratePrompt } = fields;
  const data = await adminFetch(
    `/api/admin/companions/${encodeURIComponent(ownerEmail)}/${encodeURIComponent(companionId)}`,
    { method: 'PATCH', body: JSON.stringify({ name, description, traits, appearance, avatarUrl, category, regeneratePrompt }) },
  );
  return data?.companion || null;
}

export async function adminDeleteCompanion(ownerEmail, companionId) {
  await adminFetch(
    `/api/admin/companions/${encodeURIComponent(ownerEmail)}/${encodeURIComponent(companionId)}`,
    { method: 'DELETE' },
  );
}

// ── Analytics dashboard ─────────────────────────────────────────

export async function adminAnalyticsSummary({ days = 30 } = {}) {
  const data = await adminFetch(`/api/admin/analytics/summary?days=${encodeURIComponent(days)}`);
  return data || null;
}

// ── User + credit management ────────────────────────────────────

export async function adminSearchUsers(q) {
  const data = await adminFetch(`/api/admin/users/search?q=${encodeURIComponent(q || '')}`);
  return data?.users || [];
}

export async function adminGetUser(email) {
  const data = await adminFetch(`/api/admin/users/${encodeURIComponent(email)}`);
  return data || null;
}

// amount > 0 grants tokens, amount < 0 deducts. reason is optional
// audit-log copy that shows up in the credits history.
export async function adminGrantCredits(email, { amount, reason = '' }) {
  const data = await adminFetch(`/api/admin/users/${encodeURIComponent(email)}/credits`, {
    method: 'POST',
    body: JSON.stringify({ amount, reason }),
  });
  return data?.credits || null;
}

// window: 'day' | 'month' | 'both'
export async function adminResetQuota(email, window = 'both') {
  const data = await adminFetch(`/api/admin/users/${encodeURIComponent(email)}/quota/reset`, {
    method: 'POST',
    body: JSON.stringify({ window }),
  });
  return data?.usage || null;
}
